import { direction } from 'direction';

/**
 * Enum representing text direction.
 *
 * @enum {string}
 * @property {string} LTR - Left to right text direction.
 * @property {string} RTL - Right to left text direction.
 */
export enum TextDirection {
  LTR = 'ltr',
  RTL = 'rtl'
}

/**
 * Determines the base text direction (left-to-right or right-to-left) of a given string.
 *
 * @param text - The input string whose text direction is to be determined.
 * @returns The text direction, either `TextDirection.RTL` for right-to-left text or `TextDirection.LTR` for left-to-right text.
 */
export function determineBaseTextDirection(text: string): TextDirection {
  const dir = direction(text);
  if (dir === 'rtl') {
    return TextDirection.RTL;
  } else {
    return TextDirection.LTR;
  }
}

/**
 * Determines the text direction of the provided text.
 *
 * @param text - The string to analyze for directionality.
 * @returns The detected text direction as a `TextDirection` enum value (`RTL` or `LTR`),
 *          or `undefined` if direction cannot be determined.
 */
export function determineTextDirection(text: string): TextDirection | undefined {
  const dir = direction(text);
  if (dir === 'rtl') {
    return TextDirection.RTL;
  } else if (dir === 'ltr') {
    return TextDirection.LTR;
  } else {
    return undefined;
  }
}

const bidiMirrored: Record<string, string> = {
  '(': ')',
  ')': '(',
  '<': '>',
  '>': '<',
  '[': ']',
  ']': '[',
  '{': '}',
  '}': '{',
  '/': '\\',
  '\\': '/'
  // Add more mirrored pairs as necessary
};

export function mirrorStringRtl(input: string, context: TextDirection): string {
  if (context === TextDirection.LTR) {
    return input; // No mirroring needed in LTR context
  }

  return Array.from(input)
    .map(char => bidiMirrored[char] || char) // Replace with mirrored character if available
    .join('');
}

export function reverseNeutralRunRtl(run: string, context: 'ltr' | 'rtl'): string {
  // Define a regular expression for detecting neutral characters
  const neutralRegex = /^[^\p{L}\p{N}]+$/u;

  // Check if the run is entirely neutral
  const isNeutral = neutralRegex.test(run);

  if (isNeutral && context === 'rtl') {
    // Reverse the run if it is neutral and the context is RTL
    return Array.from(run).reverse().join('');
  }

  // Return the run unchanged otherwise
  return run;
}
